import React from "react";
import type { BillStatus } from "../../hooks/useBillStatus";
import { FaEye, FaFileCirclePlus, FaPrint } from "react-icons/fa6";

interface BillActionBarProps {
	status: BillStatus;
	// "panel" is the always-visible inline bill-view column; "modal" is the
	// SideModal — same status branching, but the modal additionally supports
	// toggling between the credit note and its referenced bill in place
	// (viewingCreditNote/onViewCreditNote), where the panel only ever offers
	// a one-way "open the modal on this credit note" action.
	context: "panel" | "modal";
	onAuditLogsView: () => void;
	// Caller owns the PrintOptionsDialog open state and decides bill-vs-
	// credit-note target (via its own viewingCreditNote tracking) — this bar
	// just requests the dialog be opened.
	onPrint: () => void;
	onRetry?: () => void;
	companyDataCorrupt?: boolean;
	// True only under Model A (external app manages billing) — Model B leaves
	// credit notes creatable in BillMe same as any normal manually-owned bill.
	billCreationBlocked?: boolean;
	onAddCreditNote?: () => void;
	onViewCreditNote?: () => void;
	viewingCreditNote?: boolean;
}

// Replaces invoice-bill.page.jsx's _renderBillViewButtons (panel) and
// _renderSideModalHeaderButtons (modal), which independently re-derived the
// same canceled/ird-failed/credit-issued/normal branching — a bug fix in one
// previously had to be repeated in the other. Modal-only pseudo-states
// (viewing the audit log, adding a credit note) aren't bill statuses at all,
// so those stay handled directly in invoice-bill.page.tsx rather than here.
function BillActionBar({
	status,
	context,
	onAuditLogsView,
	onPrint,
	onRetry,
	companyDataCorrupt,
	billCreationBlocked,
	onAddCreditNote,
	onViewCreditNote,
	viewingCreditNote,
}: BillActionBarProps) {
	const containerClass = context === "panel" ? "bill-action-container" : "action-container";

	const auditLogsBtn = (
		<div className="action-btn inverted" onClick={onAuditLogsView}>
			<FaEye />
			Audit Logs
		</div>
	);

	if (status === "ird-failed") {
		return (
			<div className={containerClass}>
				{auditLogsBtn}
				<div className="action-btn" onClick={onRetry} aria-disabled={companyDataCorrupt}>
					Fix unsynced bills
				</div>
			</div>
		);
	}

	const printBtn = (
		<div className="action-btn inverted" onClick={onPrint}>
			<FaPrint />
			Print
		</div>
	);

	if (status === "canceled") {
		return (
			<div className={containerClass}>
				{auditLogsBtn}
				{printBtn}
			</div>
		);
	}

	if (status === "credit-issued") {
		return (
			<div className={containerClass}>
				{auditLogsBtn}
				{printBtn}
				<div className="action-btn" onClick={onViewCreditNote}>
					{context === "modal" && viewingCreditNote ? "View Referenced Bill" : "View CreditNote"}
				</div>
			</div>
		);
	}

	// status === "normal"
	return (
		<div className={containerClass}>
			{auditLogsBtn}
			{printBtn}
			{!billCreationBlocked && (
				<div className="action-btn" onClick={onAddCreditNote}>
					<FaFileCirclePlus />
					Credit note
				</div>
			)}
		</div>
	);
}

export default BillActionBar;
